import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { FileText, Camera, Upload, Check, AlertCircle, Wrench, X, Loader2, ClipboardList, Clock, Send, ExternalLink, CloudOff, RefreshCw } from 'lucide-react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { offlineService } from '../../services/offline.service';
import { syncService } from '../../services/sync.service';

// Get passed jobId from navigation (e.g., from Job Activity page)
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};
import { JobSelector } from '../common/JobSelector';
import { CustomSelect } from '../common/CustomSelect';
import { uploadQueueService } from '../../services/upload-queue.service';
import { isNativeApp } from '../../utils/platform.utils';
import { LocationExpiredModal } from '../common/LocationExpiredModal';
import { TMTicketForm } from './TMTicketForm';
import { TMTicketPreview } from './TMTicketPreview';
// Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

interface Job {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  lat: number;
  lng: number;
  geofence_type?: 'circle' | 'polygon';
  geofence_data?: {
    radius?: number;
    points?: Array<{ lat: number; lng: number }>;
  };
}

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  equipment_number?: string;
  bradanna_equipment_number?: string;
  serial_number?: string;
}

interface Employee {
  employee_id: number;
  first_name: string;
  last_name: string;
}

interface UnsignedTicket {
  ticket_id: number;
  ticket_number: string;
  job_name: string;
  superintendent_name: string;
  superintendent_email: string;
  unsigned_reason: string;
  created_at: string;
  submitted_by: string;
  remote_sign_expires?: string;
}

interface TicketPreviewData {
  ticket_id: number;
  ticket_number: string;
  job_name: string;
  bradanna_job_number: string;
  superintendent_name: string;
  superintendent_email: string;
  work_description: string;
  notes: string;
  start_time: string;
  stop_time: string;
  total_employee_hours: number;
  total_equipment_hours: number;
  gc_name: string;
  submitter_first_name: string;
  submitter_last_name: string;
  created_at: string;
  job_id?: number;  // For editing
  employees: Array<{ employee_id?: number; first_name: string; last_name: string; role: string; hours_worked: number }>;
  equipment: Array<{
    equipment_id?: number | null;
    equipment_name: string;
    equipment_number?: string;
    equipment_type: string;
    hours_used: number;
    is_rental: boolean;
    rental_company?: string;
    rental_equipment_type?: string;
    is_mobilized?: boolean;
  }>;
  materials: Array<{ material_type: 'import' | 'export'; description: string; quantity: number; unit: string }>;
  dumpsters: Array<{ dumpster_type: string; quantity: number }>;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Translation object
const translations = {
  en: {
    tmTickets: 'T&M Tickets',
    timeMaterialsDocumentation: 'Time & Materials Documentation',
    loading: 'Loading...',
    debug: 'Debug',
    jobSite: 'Job Site',
    autoSelectedBasedOnLocation: 'Auto-selected from your time clock entry',
    chooseJob: 'Choose a job...',
    signedTicketPhoto: 'Signed Ticket Photo',
    takePhotoOfSignedTicket: 'Take a photo of the signed ticket',
    takePhoto: 'Take Photo',
    retakePhoto: 'Retake Photo',
    equipmentUsedOptional: 'Equipment Used (Optional)',
    searchByEquipmentOrName: 'Search by equipment # or name...',
    selectedEquipment: 'Selected Equipment',
    noEquipmentSelected: 'No equipment selected. Search above to add equipment.',
    notesOptional: 'Notes (Optional)',
    addNotesPlaceholder: 'Add any additional notes about this T&M ticket...',
    submitting: 'Submitting...',
    submitTMTicket: 'Submit T&M Ticket',
    pleaseSelectJob: 'Please select a job',
    pleaseUploadSignedTicket: 'Please upload the signed ticket',
    employeeNotFound: 'Employee not found. Please log in again.',
    ticketQueued: 'T&M Ticket queued! Uploading in background...',
    reportSubmittedSuccessfully: 'Report Submitted Successfully!',
    failedToQueueTicket: 'Failed to queue ticket. Please try again.',
    failedToAccessCamera: 'Failed to access camera',
    gettingLocation: 'Getting location...',
    locationNotSupported: 'Location not supported',
    requestingLocationPermission: 'Requesting location permission...',
    noJobsLoaded: 'No jobs loaded',
    notWithinAnyGeofence: 'Not within any job geofence',
    addAttachment: 'Add Attachment',
    paperTicket: 'Paper Ticket',
    eTicket: 'E-Ticket',
    formSubmittedSuccessfully: 'T&M Ticket submitted successfully!',
    ticketQueuedSuccessfully: 'T&M Ticket saved! Will upload when online.',
    ticketNumber: 'Ticket #',
    unsignedTickets: 'Unsigned Tickets',
    noUnsignedTickets: 'No unsigned tickets',
    sendSignLink: 'Send Sign Link',
    sending: 'Sending...',
    signLinkSent: 'Signing link sent!',
    failedToSendLink: 'Failed to send link',
    close: 'Close',
    awaitingSignature: 'Awaiting Signature'
  },
  es: {
    tmTickets: 'Boletos de T&M',
    timeMaterialsDocumentation: 'Documentación de Tiempo y Materiales',
    loading: 'Cargando...',
    debug: 'Depuración',
    jobSite: 'Sitio de Trabajo',
    autoSelectedBasedOnLocation: 'Auto-seleccionado de su entrada de reloj',
    chooseJob: 'Elija un trabajo...',
    signedTicketPhoto: 'Foto del Boleto Firmado',
    takePhotoOfSignedTicket: 'Tome una foto del boleto firmado',
    takePhoto: 'Tomar Foto',
    retakePhoto: 'Volver a Tomar Foto',
    equipmentUsedOptional: 'Equipos Usados (Opcional)',
    searchByEquipmentOrName: 'Buscar por # de equipo o nombre...',
    selectedEquipment: 'Equipos Seleccionados',
    noEquipmentSelected: 'No hay equipos seleccionados. Busque arriba para agregar equipos.',
    notesOptional: 'Notas (Opcional)',
    addNotesPlaceholder: 'Agregue notas adicionales sobre este boleto de T&M...',
    submitting: 'Enviando...',
    submitTMTicket: 'Enviar Boleto de T&M',
    pleaseSelectJob: 'Por favor seleccione un trabajo',
    pleaseUploadSignedTicket: 'Por favor suba el boleto firmado',
    employeeNotFound: 'Empleado no encontrado. Favor de iniciar sesión de nuevo.',
    ticketQueued: '¡Boleto de T&M en cola! Subiendo en segundo plano...',
    reportSubmittedSuccessfully: '¡Reporte Enviado Exitosamente!',
    failedToQueueTicket: 'Error al poner en cola el boleto. Favor de intentar de nuevo.',
    failedToAccessCamera: 'Error al acceder a la cámara',
    gettingLocation: 'Obteniendo ubicación...',
    locationNotSupported: 'Ubicación no compatible',
    requestingLocationPermission: 'Solicitando permiso de ubicación...',
    noJobsLoaded: 'No hay trabajos cargados',
    notWithinAnyGeofence: 'No está dentro de ninguna geocerca de trabajo',
    addAttachment: 'Agregar Adjunto',
    paperTicket: 'Boleto Papel',
    eTicket: 'E-Boleto',
    formSubmittedSuccessfully: '¡Boleto T&M enviado exitosamente!',
    ticketQueuedSuccessfully: '¡Boleto T&M guardado! Se subirá cuando haya conexión.',
    ticketNumber: 'Boleto #',
    unsignedTickets: 'Boletos Sin Firma',
    noUnsignedTickets: 'No hay boletos sin firma',
    sendSignLink: 'Enviar Enlace',
    sending: 'Enviando...',
    signLinkSent: '¡Enlace enviado!',
    failedToSendLink: 'Error al enviar enlace',
    close: 'Cerrar',
    awaitingSignature: 'Esperando Firma'
  }
};

const TMTickets: React.FC = () => {
  // Hooks
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { location, ensureFreshLocation, forceRefreshLocation } = useLocation();

  // Navigate with location refresh (matches dashboard pattern)
  const navigateToTimeClock = async () => {
    await ensureFreshLocation();
    navigate('/time-clock');
  };

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    // Check T&M specific language first, then fall back to global language
    const tmLanguage = localStorage.getItem('tm_language_preference') as 'en' | 'es' | null;
    if (tmLanguage) return tmLanguage;
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  // Mode: photo (original) or form (new fillable form)
  // Default to 'form' (E-Ticket) as per plan
  const [mode, setMode] = useState<'photo' | 'form'>('form');
  const [formSubmittedTicketNumber, setFormSubmittedTicketNumber] = useState<string | null>(null);

  // Get translations
  const t = translations[language];
  const [jobs, setJobs] = useState<Job[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [clockedInJobId, setClockedInJobId] = useState<number | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<number[]>([]);
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);
  const [signedTicket, setSignedTicket] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Unsigned tickets state
  const [showUnsignedTickets, setShowUnsignedTickets] = useState(false);
  const [unsignedTickets, setUnsignedTickets] = useState<UnsignedTicket[]>([]);
  const [loadingUnsigned, setLoadingUnsigned] = useState(false);
  const [sendingEmailFor, setSendingEmailFor] = useState<number | null>(null);

  // In-app signing state
  const [signingTicket, setSigningTicket] = useState<UnsignedTicket | null>(null);
  const [signingPreviewData, setSigningPreviewData] = useState<TicketPreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const signatureCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const lastPosRef = React.useRef<{ x: number; y: number } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Editing unsigned ticket state
  const [editingTicketData, setEditingTicketData] = useState<TicketPreviewData | null>(null);
  const [editingTicketJobId, setEditingTicketJobId] = useState<number | null>(null);

  // Pending (offline queue) tickets state
  const [pendingQueueCount, setPendingQueueCount] = useState(0);
  const [isSyncingQueue, setIsSyncingQueue] = useState(false);

  // Loading states
  const [isInitializing, setIsInitializing] = useState(true);

  // Location state for PWA location refresh
  const [locationRefreshing, setLocationRefreshing] = useState(false);
  // Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx

  // Track visibility changes to re-check location staleness when PWA resumes
  const [visibilityKey, setVisibilityKey] = useState(0);

  // Listen for page visibility changes (iPad sleep/wake, tab switch)
  useEffect(() => {
    if (isNativeApp()) return; // Native apps handle this in LocationContext

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('T&M Tickets: Page became visible, re-checking location staleness');
        setVisibilityKey(prev => prev + 1); // Force re-check
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Check for stale location (> 5 minutes old) - PWA only
  const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
  const isLocationStale = useMemo(() => {
    if (isNativeApp()) return false; // Native apps auto-refresh on resume
    if (!location) return true;
    return (Date.now() - location.timestamp) > STALE_THRESHOLD;
  }, [location, visibilityKey]); // Re-check when page becomes visible

  // Note: Job mismatch detection is handled globally by GlobalMismatchChecker in App.tsx

  // Handler for refreshing location (PWA)
  const handleRefreshLocation = async () => {
    setLocationRefreshing(true);
    try {
      await forceRefreshLocation();
    } finally {
      setLocationRefreshing(false);
    }
  };

  // Handler for T&M language toggle (overrides device language)
  const handleLanguageToggle = (newLang: 'en' | 'es') => {
    setLanguage(newLang);
    localStorage.setItem('tm_language_preference', newLang);
  };

  // Handler for form submission success
  const handleFormSubmit = (ticketNumber: string) => {
    setFormSubmittedTicketNumber(ticketNumber);
    setShowSuccessModal(true);
    // No auto-close - user must click OK to return to dashboard
  };

  // Handler for success modal OK button
  const handleSuccessModalOk = () => {
    setShowSuccessModal(false);
    setFormSubmittedTicketNumber(null);
    navigate('/');  // Return to dashboard
  };

  // Handler for form cancel
  const handleFormCancel = () => {
    setMode('photo');
  };

  // Load pending queue count
  const loadPendingQueueCount = useCallback(async () => {
    try {
      const count = await offlineService.getPendingTMTicketCount();
      setPendingQueueCount(count);
    } catch (error) {
      console.error('Error loading pending queue count:', error);
    }
  }, []);

  // Sync pending queue
  const handleSyncPendingQueue = async () => {
    if (isSyncingQueue || !navigator.onLine) return;
    setIsSyncingQueue(true);
    try {
      const result = await syncService.syncPendingTMTickets();
      if (result.success > 0) {
        setMessage({ type: 'success', text: language === 'en'
          ? `${result.success} ticket(s) synced successfully`
          : `${result.success} ticket(s) sincronizado(s) exitosamente` });
        setTimeout(() => setMessage(null), 3000);
      }
      await loadPendingQueueCount();
    } catch (error) {
      console.error('Error syncing pending queue:', error);
    } finally {
      setIsSyncingQueue(false);
    }
  };

  // Load pending queue count on mount and when visibility changes
  useEffect(() => {
    loadPendingQueueCount();
  }, [loadPendingQueueCount, visibilityKey]);

  // Load unsigned tickets
  const loadUnsignedTickets = async () => {
    setLoadingUnsigned(true);
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/unsigned`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setUnsignedTickets(result.data || []);
      }
    } catch (error) {
      console.error('Error loading unsigned tickets:', error);
    } finally {
      setLoadingUnsigned(false);
    }
  };

  // Send signing email
  const sendSigningEmail = async (ticketId: number, superintendentEmail: string) => {
    setSendingEmailFor(ticketId);
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/${ticketId}/send-sign-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: superintendentEmail })
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: t.signLinkSent });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.error || t.failedToSendLink });
      }
    } catch (error) {
      console.error('Error sending signing email:', error);
      setMessage({ type: 'error', text: t.failedToSendLink });
    } finally {
      setSendingEmailFor(null);
    }
  };

  // Open unsigned tickets modal
  const handleShowUnsignedTickets = async () => {
    setShowUnsignedTickets(true);
    await loadUnsignedTickets();
  };

  // Start signing a ticket - fetch preview data and show TMTicketPreview
  const startSigning = async (ticket: UnsignedTicket) => {
    setSigningTicket(ticket);
    setIsLoadingPreview(true);
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/${ticket.ticket_id}/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setSigningPreviewData(result.data);
        setShowUnsignedTickets(false); // Close the unsigned tickets modal
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to load ticket preview' });
        setSigningTicket(null);
      }
    } catch (error) {
      console.error('Error loading ticket preview:', error);
      setMessage({ type: 'error', text: 'Failed to load ticket preview' });
      setSigningTicket(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Canvas drawing functions
  const getCanvasPosition = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPosRef.current = getCanvasPosition(e);
    setHasSignature(true);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !lastPosRef.current) return;
    const pos = getCanvasPosition(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPosRef.current = pos;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
  };

  // Handle signature submission from TMTicketPreview
  const handlePreviewSign = async (signatureBase64: string) => {
    if (!signingTicket) return;
    setIsSubmittingSignature(true);
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/${signingTicket.ticket_id}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ signature_base64: signatureBase64 })
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: language === 'en' ? 'Ticket signed successfully!' : 'Boleto firmado con éxito!' });
        setSigningTicket(null);
        setSigningPreviewData(null);
        // Remove from unsigned list
        setUnsignedTickets(prev => prev.filter(t => t.ticket_id !== signingTicket.ticket_id));
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to sign ticket' });
      }
    } catch (error) {
      console.error('Error signing ticket:', error);
      setMessage({ type: 'error', text: 'Failed to sign ticket' });
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  // Submit signature (old canvas-based method - keeping for reference)
  const submitSignature = async () => {
    if (!signingTicket || !hasSignature) return;
    setIsSubmittingSignature(true);
    try {
      const canvas = signatureCanvasRef.current;
      if (!canvas) throw new Error('Canvas not found');
      const signatureBase64 = canvas.toDataURL('image/png');
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/${signingTicket.ticket_id}/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ signature_base64: signatureBase64 })
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: language === 'en' ? 'Ticket signed!' : 'Boleto firmado!' });
        setSigningTicket(null);
        // Remove from list
        setUnsignedTickets(prev => prev.filter(t => t.ticket_id !== signingTicket.ticket_id));
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to sign ticket' });
      }
    } catch (error) {
      console.error('Error signing ticket:', error);
      setMessage({ type: 'error', text: 'Failed to sign ticket' });
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };

    window.addEventListener('languageChange', handleLanguageChange);
    const savedLanguage = localStorage.getItem('language') as 'en' | 'es' | null;
    if (savedLanguage === 'en' || savedLanguage === 'es') {
      setLanguage(savedLanguage);
    }

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Load user
        loadEmployee();

        // Load jobs and equipment
        await loadJobs();

        await loadEquipment();

        // Load unsigned tickets count (for badge/button visibility)
        loadUnsignedTickets();

        uploadQueueService.startAutoSync();
      } catch (err) {
        console.error('Error initializing TMTickets:', err);
        setIsInitializing(false);
      }
    };

    initializeData();

    return () => {
      // Don't stop sync here - it's managed app-wide now
    };
  }, []);

  // Get passed jobId from navigation (e.g., from Job Activity page)
  const passedJobId = usePassedJobId();

  // Check clocked-in job first, then use GPS if needed
  useEffect(() => {
    if (jobs.length > 0) {
      checkClockedJobFirst();
    }
  }, [jobs, passedJobId]);

  const checkClockedJobFirst = async () => {
    try {
      // Check if a job was passed via navigation (from Job Activity page)
      if (passedJobId) {
        const passedJob = jobs.find((job: Job) => job.job_id === passedJobId);
        if (passedJob) {
          console.log('[TMTickets] Job passed via navigation:', passedJob.job_name);
          setSelectedJob(passedJob.job_id);
          setSelectedJobName(passedJob.job_name);
          setIsInitializing(false);
          return;
        }
      }

      // Check if user is clocked in
      await checkClockedJob();

      // For admins not clocked in: JobSelector will handle job selection
      // For workers/foremen not clocked in: Show "Please clock in" message
      setIsInitializing(false);
    } catch (err) {
      console.error('Error checking clocked job:', err);
      setIsInitializing(false);
    }
  };

  // Filter equipment based on search
  useEffect(() => {
    if (equipmentSearch.trim()) {
      const filtered = equipment.filter(eq =>
        eq.bradanna_equipment_number?.toLowerCase().includes(equipmentSearch.toLowerCase()) ||
        eq.equipment_name.toLowerCase().includes(equipmentSearch.toLowerCase()) ||
        eq.equipment_number?.toLowerCase().includes(equipmentSearch.toLowerCase()) ||
        eq.serial_number?.toLowerCase().includes(equipmentSearch.toLowerCase())
      );
      setFilteredEquipment(filtered);
    } else {
      setFilteredEquipment([]);
    }
  }, [equipmentSearch, equipment]);

  // Load previous workday equipment when job changes
  useEffect(() => {
    if (selectedJob) {
      loadPreviousWorkdayEquipment();
    }
  }, [selectedJob]);

  const loadEmployee = () => {
    const employeeData = localStorage.getItem('bfa_user');
    if (employeeData) {
      setEmployee(JSON.parse(employeeData));
    }
  };

  const loadJobs = async () => {
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        // Filter to active jobs only
        const activeJobs = result.data.filter((job: any) => job.is_active);
        setJobs(activeJobs);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadEquipment = async () => {
    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setEquipment(result.data);
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const loadPreviousWorkdayEquipment = async () => {
    if (!selectedJob) return;

    try {
      // Calculate previous workday (handle weekends)
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
      let daysBack = 1; // Default to yesterday

      // If Monday, go back to Friday (3 days)
      if (dayOfWeek === 1) {
        daysBack = 3;
      }
      // If Sunday, go back to Friday (2 days)
      else if (dayOfWeek === 0) {
        daysBack = 2;
      }

      const previousWorkday = new Date();
      previousWorkday.setDate(today.getDate() - daysBack);

      // Format date as YYYY-MM-DD in local timezone
      const year = previousWorkday.getFullYear();
      const month = String(previousWorkday.getMonth() + 1).padStart(2, '0');
      const day = String(previousWorkday.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const token = localStorage.getItem('bfa_token');
      const response = await fetch(
        `${API_BASE_URL}/api/tm-tickets/previous-equipment?job_id=${selectedJob}&date=${dateStr}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        const equipIds = result.data.map((eq: any) => eq.equipment_id);
        setSelectedEquipment(equipIds);
      }
    } catch (error) {
      console.error('Error loading previous workday equipment:', error);
      // Don't show error to user, just fail silently
    }
  };

  // Geofencing helper functions
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const isInsidePolygon = (userLat: number, userLng: number, points: Array<{ lat: number; lng: number }>): boolean => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].lat, yi = points[i].lng;
      const xj = points[j].lat, yj = points[j].lng;

      const intersect = ((yi > userLng) !== (yj > userLng))
          && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isInsideCircle = (userLat: number, userLng: number, centerLat: number, centerLng: number, radius: number): boolean => {
    const distance = calculateDistance(userLat, userLng, centerLat, centerLng);
    return distance <= radius;
  };

  const isUserWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return isInsideCircle(userLat, userLng, job.lat, job.lng, job.geofence_data.radius);
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return isInsidePolygon(userLat, userLng, job.geofence_data.points);
    }
    // Default circular geofence of 100 meters
    const distance = calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  }, []);

  const checkClockedJob = async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/time/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.job_id) {
          const jobId = data.data.job_id;
          setClockedInJobId(jobId); // Store clocked-in job for sorting
          const job = jobs.find(j => j.job_id === jobId);
          if (job) {
            setSelectedJob(job.job_id);
            setSelectedJobName(job.job_name);
            // Note: Job mismatch detection is handled globally by GlobalMismatchChecker
            return true;
          }
        }
      }
      // Not clocked in
      return false;
    } catch (error) {
      console.error('Error checking clocked job:', error);
      return false;
    }
  };

  // Photo compression
  const compressImage = useCallback((file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onerror = () => reject(new Error('Failed to load image'));

      img.onload = () => {
        try {
          // Resize to max 1200px for good quality without huge files
          const maxDimension = 1200;
          let width = img.width;
          let height = img.height;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;

          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
              (blob) => {
                URL.revokeObjectURL(img.src);

                if (blob) {
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolve(compressedFile);
                } else {
                  resolve(file);
                }
              },
              'image/jpeg',
              0.95 // High quality for OCR
            );
          } else {
            resolve(file);
          }
        } catch (error) {
          console.error('Error during image compression:', error);
          resolve(file);
        }
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  const handleSignedTicketCapture = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';

      input.onchange = async (e: any) => {
        const file = e.target?.files?.[0];
        if (file) {
          // Compress image before storing
          const compressed = await compressImage(file);
          setSignedTicket(compressed);
          const url = URL.createObjectURL(compressed);
          setPreviewUrl(url);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error capturing photo:', error);
      setMessage({ type: 'error', text: t.failedToAccessCamera });
    }
  };

  const handleAttachmentCapture = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.multiple = true;

      input.onchange = async (e: any) => {
        const files = Array.from(e.target?.files || []) as File[];
        // Compress each file
        const compressed = await Promise.all(files.map(f => compressImage(f)));
        setAttachments(prev => [...prev, ...compressed]);
      };

      input.click();
    } catch (error) {
      console.error('Error capturing attachments:', error);
      setMessage({ type: 'error', text: t.failedToAccessCamera });
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedJob) {
      setMessage({ type: 'error', text: t.pleaseSelectJob });
      return;
    }

    if (!signedTicket) {
      setMessage({ type: 'error', text: t.pleaseUploadSignedTicket });
      return;
    }

    if (!employee) {
      setMessage({ type: 'error', text: t.employeeNotFound });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare files for upload queue
      const files: { file: Blob; filename: string; fieldName: string }[] = [
        { file: signedTicket, filename: signedTicket.name, fieldName: 'signed_ticket' }
      ];

      // Add attachments to files array
      attachments.forEach((file, index) => {
        files.push({ file, filename: file.name, fieldName: 'attachments' });
      });

      // Add to queue (saves immediately, uploads in background)
      await uploadQueueService.addToQueue(
        'tm_ticket',
        {
          job_id: selectedJob,
          employee_id: employee.employee_id,
          equipment_ids: JSON.stringify(selectedEquipment),
          notes: notes || '',
          ticket_number: `TM-${Date.now()}`
        },
        files,
        employee.employee_id,
        selectedJob
      );

      // Show success modal popup
      setShowSuccessModal(true);

      // Auto-hide modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);

      // Reset form (keep selected job - user is still clocked in)
      setTimeout(() => {
        setSelectedEquipment([]);
        setSignedTicket(null);
        setAttachments([]);
        setNotes('');
        setPreviewUrl(null);
        setMessage(null);
      }, 2000);

    } catch (error) {
      console.error('Error queuing T&M ticket:', error);
      setMessage({ type: 'error', text: t.failedToQueueTicket });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading overlay while initializing
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {t.loading}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Not clocked in check - workers/foremen must be clocked in
  if (!selectedJob) {
    const isAdmin = auth?.user?.role === 'admin';

    // Workers/foremen must clock in first
    if (!isAdmin) {
      return (
        <div style={{
          minHeight: 'calc(100vh - 56px)',
          background: '#f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <AlertCircle size={48} style={{ color: '#f59e0b', marginBottom: '16px' }} />
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>
              {language === 'en' ? 'Clock In Required' : 'Debe Fichar'}
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', lineHeight: '1.5' }}>
              {language === 'en'
                ? 'You must be clocked in to submit T&M tickets'
                : 'Debe estar fichado para enviar boletos de T&M'}
            </p>
            <button
              onClick={navigateToTimeClock}
              style={{
                background: '#3b82f6',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              {language === 'en' ? 'Go to Time Clock' : 'Ir al Reloj'}
            </button>
          </div>
        </div>
      );
    }
    // Admin can manually select job - continue to show the form with dropdown
  }

  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)',
      background: '#f0f2f5',
      paddingTop: '40px',
      paddingBottom: '80px'
    }}>
      {/* Location Expired Modal (PWA only) */}
      {isLocationStale && !isNativeApp() && !locationRefreshing && (
        <LocationExpiredModal
          onRefresh={handleRefreshLocation}
          language={language}
        />
      )}

      {/* Note: Job mismatch modal is handled globally by GlobalMismatchChecker in App.tsx */}

      {/* Header */}
      <div style={{
        background: 'white',
        padding: '20px',
        marginBottom: '16px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={28} color="#3b82f6" />
            <div>
              <h1 style={{ margin: 0, fontSize: '22px', color: '#1f2937', fontWeight: '600' }}>{t.tmTickets}</h1>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{t.timeMaterialsDocumentation}</p>
            </div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '16px',
          padding: '0 20px'
        }}>
          <button
            onClick={() => setMode('form')}
            style={{
              flex: 1,
              padding: '12px 16px',
              backgroundColor: mode === 'form' ? '#3b82f6' : 'white',
              color: mode === 'form' ? 'white' : '#374151',
              border: mode === 'form' ? 'none' : '2px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <ClipboardList size={20} />
            {t.eTicket}
          </button>
          <button
            onClick={() => setMode('photo')}
            style={{
              flex: 1,
              padding: '12px 16px',
              backgroundColor: mode === 'photo' ? '#3b82f6' : 'white',
              color: mode === 'photo' ? 'white' : '#374151',
              border: mode === 'photo' ? 'none' : '2px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Camera size={20} />
            {t.paperTicket}
          </button>
        </div>

        {/* Pending Queue Indicator - show when there are queued offline tickets */}
        {pendingQueueCount > 0 && (
          <button
            onClick={handleSyncPendingQueue}
            disabled={isSyncingQueue || !navigator.onLine}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px 16px',
              backgroundColor: navigator.onLine ? '#dbeafe' : '#fee2e2',
              color: navigator.onLine ? '#1e40af' : '#991b1b',
              border: `2px solid ${navigator.onLine ? '#3b82f6' : '#ef4444'}`,
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: navigator.onLine ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: isSyncingQueue ? 0.7 : 1
            }}
          >
            {isSyncingQueue ? (
              <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            ) : navigator.onLine ? (
              <RefreshCw size={18} />
            ) : (
              <CloudOff size={18} />
            )}
            {pendingQueueCount} {language === 'en' ? 'Ticket(s) Pending Upload' : 'Ticket(s) Pendiente(s)'}
            {navigator.onLine && !isSyncingQueue && (
              <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                ({language === 'en' ? 'Tap to sync' : 'Toca para sincronizar'})
              </span>
            )}
          </button>
        )}

        {/* Unsigned Tickets Button - only show if there are unsigned tickets */}
        {unsignedTickets.length > 0 && (
          <button
            onClick={handleShowUnsignedTickets}
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px 16px',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              border: '2px solid #f59e0b',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Clock size={18} />
            {t.unsignedTickets} ({unsignedTickets.length})
          </button>
        )}
      </div>

      {/* Unsigned Tickets Modal */}
      {showUnsignedTickets && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#fef3c7'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Clock size={24} color="#92400e" />
                <h2 style={{ margin: 0, fontSize: '18px', color: '#92400e', fontWeight: '700' }}>
                  {t.unsignedTickets}
                </h2>
              </div>
              <button
                onClick={() => setShowUnsignedTickets(false)}
                style={{
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '8px'
                }}
              >
                <X size={24} color="#92400e" />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px'
            }}>
              {loadingUnsigned ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6b7280' }} />
                </div>
              ) : unsignedTickets.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#6b7280'
                }}>
                  <Clock size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p style={{ margin: 0 }}>{t.noUnsignedTickets}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {unsignedTickets.map(ticket => (
                    <div
                      key={ticket.ticket_id}
                      style={{
                        padding: '16px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '12px',
                        border: '1px solid #fcd34d'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '700', color: '#92400e', fontSize: '16px' }}>
                            {ticket.ticket_number}
                          </div>
                          <div style={{ fontSize: '13px', color: '#78350f', marginTop: '2px' }}>
                            {ticket.job_name}
                          </div>
                        </div>
                        <div style={{
                          padding: '4px 8px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {t.awaitingSignature}
                        </div>
                      </div>

                      <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                        <div><strong>{language === 'en' ? 'Superintendent:' : 'Superintendente:'}</strong> {ticket.superintendent_name}</div>
                        <div><strong>{language === 'en' ? 'Email:' : 'Correo:'}</strong> {ticket.superintendent_email}</div>
                        <div><strong>{language === 'en' ? 'Reason:' : 'Razón:'}</strong> {ticket.unsigned_reason}</div>
                        <div><strong>{language === 'en' ? 'Created:' : 'Creado:'}</strong> {new Date(ticket.created_at).toLocaleString()}</div>
                      </div>

                      <button
                        onClick={() => startSigning(ticket)}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          backgroundColor: '#22c55e',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        <Check size={20} />
                        {language === 'en' ? 'Sign Now' : 'Firmar Ahora'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowUnsignedTickets(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signing Screen - TMTicketPreview */}
      {signingTicket && (
        isLoadingPreview ? (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f3f4f6',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
            <p style={{ marginTop: '16px', color: '#6b7280' }}>
              {language === 'en' ? 'Loading ticket...' : 'Cargando boleto...'}
            </p>
          </div>
        ) : signingPreviewData ? (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1001,
            overflowY: 'auto'
          }}>
            <TMTicketPreview
              jobName={signingPreviewData.job_name}
              jobNumber={signingPreviewData.bradanna_job_number}
              submittedBy={`${signingPreviewData.submitter_first_name} ${signingPreviewData.submitter_last_name}`}
              date={new Date(signingPreviewData.start_time || signingPreviewData.created_at || new Date())}
              superintendentName={signingPreviewData.superintendent_name}
              superintendentEmail={signingPreviewData.superintendent_email}
              employees={signingPreviewData.employees.map(e => ({
                name: `${e.first_name} ${e.last_name}`,
                role: e.role,
                hours: e.hours_worked
              }))}
              equipment={signingPreviewData.equipment.map(e => ({
                name: e.equipment_name,
                type: e.equipment_type || '',
                hours: e.hours_used,
                isMobilized: e.is_mobilized || false,
                isRental: e.is_rental || false,
                rentalCompany: e.rental_company
              }))}
              materials={signingPreviewData.materials.map(m => ({
                type: m.material_type,
                description: m.description,
                quantity: m.quantity,
                unit: m.unit
              }))}
              dumpsters={signingPreviewData.dumpsters.map(d => ({
                dumpster_type: d.dumpster_type,
                size: '',
                quantity: d.quantity
              }))}
              notes={signingPreviewData.notes || ''}
              totalEmployeeHours={signingPreviewData.total_employee_hours || 0}
              totalEquipmentHours={signingPreviewData.total_equipment_hours || 0}
              language={language}
              onEdit={() => {
                // Switch to edit mode with the ticket data
                if (signingPreviewData?.job_id) {
                  setEditingTicketData(signingPreviewData);
                  setEditingTicketJobId(signingPreviewData.job_id);
                }
                setSigningTicket(null);
                setSigningPreviewData(null);
              }}
              onSign={handlePreviewSign}
              workDescription={signingPreviewData.work_description}
              gcName={signingPreviewData.gc_name}
              startTime={signingPreviewData.start_time ? new Date(signingPreviewData.start_time) : undefined}
              stopTime={signingPreviewData.stop_time ? new Date(signingPreviewData.stop_time) : undefined}
            />
          </div>
        ) : null
      )}

      <div style={{ padding: '0 16px' }}>
        {/* Message */}
        {message && (
          <div style={{
            background: message.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            padding: '16px',
            borderRadius: '12px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            {message.type === 'success' ? <Check size={24} /> : <AlertCircle size={24} />}
            <span style={{ fontWeight: '500', flex: 1 }}>{message.text}</span>
          </div>
        )}

        {/* Editing Unsigned Ticket - Show TMTicketForm with initialData */}
        {editingTicketData && editingTicketJobId && employee && (
          <TMTicketForm
            jobId={editingTicketJobId}
            jobName={editingTicketData.job_name}
            employeeId={employee.employee_id}
            language={language}
            onSubmit={(ticketNumber) => {
              // Save ticket_id before clearing state
              const editedTicketId = editingTicketData.ticket_id;
              setEditingTicketData(null);
              setEditingTicketJobId(null);
              handleFormSubmit(ticketNumber);
              // Remove the old unsigned ticket from the list
              setUnsignedTickets(prev => prev.filter(t => t.ticket_id !== editedTicketId));
            }}
            onCancel={() => {
              setEditingTicketData(null);
              setEditingTicketJobId(null);
            }}
            initialData={{
              ticket_id: editingTicketData.ticket_id,
              ticket_number: editingTicketData.ticket_number,
              superintendent_name: editingTicketData.superintendent_name,
              superintendent_email: editingTicketData.superintendent_email,
              work_description: editingTicketData.work_description || '',
              notes: editingTicketData.notes || '',
              start_time: editingTicketData.start_time,
              stop_time: editingTicketData.stop_time,
              gc_name: editingTicketData.gc_name || '',
              employees: editingTicketData.employees.map(e => ({
                employee_id: e.employee_id || 0,
                first_name: e.first_name,
                last_name: e.last_name,
                role: e.role,
                hours_worked: e.hours_worked
              })),
              equipment: editingTicketData.equipment.map(e => ({
                equipment_id: e.equipment_id || null,
                equipment_name: e.equipment_name,
                equipment_number: e.equipment_number || '',
                equipment_type: e.equipment_type || '',
                hours_used: e.hours_used,
                is_rental: e.is_rental || false,
                rental_company: e.rental_company,
                rental_equipment_type: e.rental_equipment_type,
                is_mobilized: e.is_mobilized || false
              })),
              materials: editingTicketData.materials,
              dumpsters: editingTicketData.dumpsters
            }}
          />
        )}

        {/* Form Mode - Show TMTicketForm */}
        {mode === 'form' && selectedJob && employee && !editingTicketData && (
          <TMTicketForm
            jobId={selectedJob}
            jobName={selectedJobName}
            employeeId={employee.employee_id}
            language={language}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        )}

        {/* Form Mode - No job selected yet */}
        {mode === 'form' && !selectedJob && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <label style={{
              display: 'block',
              color: '#374151',
              fontWeight: '600',
              marginBottom: '12px',
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t.jobSite} *
            </label>
            <CustomSelect
              value={selectedJob || ''}
              onChange={(value) => {
                const jobId = Number(value);
                setSelectedJob(jobId);
                const job = jobs.find(j => j.job_id === jobId);
                if (job) setSelectedJobName(job.job_name);
              }}
              placeholder={t.chooseJob}
              options={[...jobs]
                .sort((a, b) => {
                  // Put clocked-in job first
                  if (a.job_id === clockedInJobId) return -1;
                  if (b.job_id === clockedInJobId) return 1;
                  return 0;
                })
                .map(job => ({
                  value: job.job_id,
                  label: `${job.bradanna_job_number ? `${job.bradanna_job_number} - ` : ''}${job.job_name}${job.job_id === clockedInJobId ? ' (Current)' : ''}`
                }))}
            />
          </div>
        )}

        {/* Photo Mode - Original Photo Upload UI */}
        {mode === 'photo' && (
          <>
        {/* Job Selection */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <label style={{
            display: 'block',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '12px',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t.jobSite} *
          </label>
          {selectedJobName ? (
            <div>
              <div style={{
                padding: '12px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
                color: '#1f2937',
                marginBottom: '8px'
              }}>
                {selectedJobName}
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                {t.autoSelectedBasedOnLocation}
              </p>
            </div>
          ) : (
            <CustomSelect
              value={selectedJob || ''}
              onChange={(value) => {
                const jobId = Number(value);
                setSelectedJob(jobId);
                const job = jobs.find(j => j.job_id === jobId);
                if (job) setSelectedJobName(job.job_name);
              }}
              placeholder={t.chooseJob}
              options={[...jobs]
                .sort((a, b) => {
                  // Put clocked-in job first
                  if (a.job_id === clockedInJobId) return -1;
                  if (b.job_id === clockedInJobId) return 1;
                  return 0;
                })
                .map(job => ({
                  value: job.job_id,
                  label: `${job.bradanna_job_number ? `${job.bradanna_job_number} - ` : ''}${job.job_name}${job.job_id === clockedInJobId ? ' (Current)' : ''}`
                }))}
            />
          )}
        </div>

        {/* Signed Ticket Photo */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <label style={{
            display: 'block',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '12px',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t.signedTicketPhoto} *
          </label>

          {!signedTicket ? (
            <div style={{
              backgroundColor: '#f8fafc',
              border: '2px dashed #d1d5db',
              borderRadius: '12px',
              padding: '40px 32px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <Camera size={48} color="#9ca3af" />
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                {t.takePhotoOfSignedTicket}
              </p>
              <button
                onClick={handleSignedTicketCapture}
                style={{
                  backgroundColor: '#22c55e',
                  color: 'white',
                  padding: '14px 32px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(34,197,94,0.3)'
                }}
              >
                <Camera size={20} />
                {t.takePhoto}
              </button>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <img
                  src={previewUrl || ''}
                  alt="Signed Ticket"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    display: 'block'
                  }}
                />
              </div>
              <button
                onClick={() => {
                  setSignedTicket(null);
                  setPreviewUrl(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 8px rgba(239,68,68,0.3)'
                }}
              >
                <X size={20} />
                Remove Photo
              </button>
            </div>
          )}
        </div>

        {/* Equipment Selection */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '12px',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <Wrench size={18} />
            {t.equipmentUsedOptional}
          </label>

          {/* Search Bar */}
          <div style={{ marginBottom: '16px', position: 'relative' }}>
            <input
              type="text"
              value={equipmentSearch}
              onChange={(e) => setEquipmentSearch(e.target.value)}
              placeholder={t.searchByEquipmentOrName}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#1f2937'
              }}
            />

            {/* Search Results Dropdown */}
            {filteredEquipment.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                backgroundColor: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}>
                {filteredEquipment.map(equip => (
                  <div
                    key={equip.equipment_id}
                    onClick={() => {
                      if (!selectedEquipment.includes(equip.equipment_id)) {
                        setSelectedEquipment(prev => [...prev, equip.equipment_id]);
                      }
                      setEquipmentSearch('');
                    }}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      backgroundColor: selectedEquipment.includes(equip.equipment_id) ? '#f3f4f6' : 'white'
                    }}
                  >
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>{equip.equipment_name}</div>
                    {equip.bradanna_equipment_number && (
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>#{equip.bradanna_equipment_number}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Equipment Chips */}
          <div>
            <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
              {t.selectedEquipment} ({selectedEquipment.length})
            </p>
            {selectedEquipment.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#6b7280', fontStyle: 'italic' }}>
                {t.noEquipmentSelected}
              </p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedEquipment.map(equipId => {
                  const equip = equipment.find(e => e.equipment_id === equipId);
                  if (!equip) return null;
                  return (
                    <div
                      key={equipId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#eff6ff',
                        border: '2px solid #3b82f6',
                        borderRadius: '20px',
                        fontSize: '14px'
                      }}
                    >
                      <span style={{ fontWeight: '500', color: '#1f2937' }}>{equip.equipment_name}</span>
                      {equip.bradanna_equipment_number && (
                        <span style={{ color: '#6b7280' }}>#{equip.bradanna_equipment_number}</span>
                      )}
                      <button
                        onClick={() => setSelectedEquipment(prev => prev.filter(id => id !== equipId))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          fontSize: '18px',
                          lineHeight: 1,
                          padding: 0,
                          fontWeight: 'bold'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Attachments (Receipts/Documents) */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <label style={{
            display: 'block',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '12px',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Receipts/Documents (Optional)
          </label>

          {attachments.length > 0 && (
            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {attachments.map((file, index) => (
                <div key={index} style={{
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>{file.name}</span>
                  <button
                    onClick={() => removeAttachment(index)}
                    style={{
                      padding: '6px 12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleAttachmentCapture}
            style={{
              width: '100%',
              padding: '12px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Camera size={20} />
            {t.addAttachment} ({attachments.length})
          </button>
        </div>

        {/* Notes */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <label style={{
            display: 'block',
            color: '#374151',
            fontWeight: '600',
            marginBottom: '12px',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {t.notesOptional}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t.addNotesPlaceholder}
            rows={4}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              resize: 'vertical',
              fontFamily: 'inherit',
              backgroundColor: 'white',
              color: '#1f2937'
            }}
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedJob || !signedTicket}
          style={{
            width: '100%',
            padding: '16px',
            background: isSubmitting || !selectedJob || !signedTicket
              ? '#d1d5db'
              : '#22c55e',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '18px',
            fontWeight: '700',
            cursor: isSubmitting || !selectedJob || !signedTicket ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px',
            transition: 'all 0.2s'
          }}
        >
          {isSubmitting ? (
            <>
              <Upload className="animate-spin" size={24} />
              {t.submitting}
            </>
          ) : (
            <>
              <Upload size={24} />
              {t.submitTMTicket}
            </>
          )}
        </button>
          </>
        )}
      </div>

      {/* Success Modal Popup */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            animation: 'slideIn 0.3s ease-out'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: formSubmittedTicketNumber?.startsWith('QUEUED-') ? '#3b82f6' : '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              animation: 'scaleIn 0.5s ease-out'
            }}>
              {formSubmittedTicketNumber?.startsWith('QUEUED-') ? (
                <CloudOff size={48} color="white" strokeWidth={2} />
              ) : (
                <Check size={48} color="white" strokeWidth={3} />
              )}
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '12px'
            }}>
              {formSubmittedTicketNumber?.startsWith('QUEUED-')
                ? t.ticketQueuedSuccessfully
                : formSubmittedTicketNumber
                  ? t.formSubmittedSuccessfully
                  : t.reportSubmittedSuccessfully}
            </h2>
            {formSubmittedTicketNumber && !formSubmittedTicketNumber.startsWith('QUEUED-') && (
              <p style={{
                fontSize: '18px',
                color: '#3b82f6',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                {t.ticketNumber}{formSubmittedTicketNumber}
              </p>
            )}
            {formSubmittedTicketNumber ? (
              <button
                type="button"
                onClick={handleSuccessModalOk}
                style={{
                  marginTop: '20px',
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            ) : (
              <p style={{
                fontSize: '16px',
                color: '#6b7280',
                marginBottom: '0'
              }}>
                {t.ticketQueued}
              </p>
            )}
          </div>
          <style>{`
            @keyframes slideIn {
              from {
                transform: translateY(-50px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            @keyframes scaleIn {
              from {
                transform: scale(0);
              }
              to {
                transform: scale(1);
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default TMTickets;
